'use client'

import type { ButtonHTMLAttributes, ReactElement, ReactNode, Ref } from 'react'
import { cloneElement, isValidElement } from 'react'
import { Button as SaiButton } from '@sai/ui/components'
import { cn } from '@/lib/utils'

// ============================================================================
// Types
// ============================================================================

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'secondary' | 'outline' | 'ghost' | 'destructive'
  size?: 'default' | 'sm' | 'lg' | 'icon'
  ref?: Ref<HTMLButtonElement>
  /**
   * Render the CHILD element with this button's styling instead of a
   * `<button>` — sai-ui's own `asChild`, forwarded.
   *
   * The case it exists for is a NAVIGATION that should look like a button,
   * written inside a CLIENT component:
   * `<Button asChild><Link href="…">Open the room</Link></Button>`. Before
   * this, such a call site hand-rolled the shape in `className`, and a
   * hand-rolled shape does not follow the design system — `SIZE_GEOMETRY`,
   * the variant fills and the pill radius all live HERE, so a copy stayed
   * square and squat while every real button went pill-shaped. That
   * divergence is exactly what this adapter
   * exists to prevent, and the prop signature being "frozen" was never meant
   * to mean call sites should route around it.
   *
   * `type` is withheld when this is set: it is meaningless on an anchor, and
   * the child supplies its own element. It is also NEUTRALISED ON THE CHILD —
   * sai-ui hardcodes `type: "button"` inside the package and its Slot clones
   * the child with it, which is invalid on an `<a>` (#13862). See
   * `withoutVendorButtonOnlyProps`.
   *
   * **NOT FROM A SERVER COMPONENT — use `ButtonLink` there (#13862).** This
   * path renders through Radix's `Slot`, which inspects its child
   * SYNCHRONOUSLY with `isValidElement` and returns `null` for anything else.
   * A child handed from a server component to a client one arrives as a Flight
   * reference that can still be an unresolved `react.lazy` when SSR runs, so
   * the control is MISSING FROM THE SERVER HTML and appears only after
   * hydration — silently, with no error and no fallback. That is what
   * `/meetings/[id]` hit: the whole subtree was discarded and re-rendered on
   * the client every load. `ButtonLink` carries the same visual contract
   * (`buttonClassName`, below) with no Slot and nothing to resolve.
   */
  asChild?: boolean
}

// ============================================================================
// Adapter over @sai/ui (#13204, Stage 4 slice 3 of #13197)
// ============================================================================
//
// `@/components/ui/button` stays the permanent import path and its PROP
// SIGNATURE IS FROZEN — the ~74 consumer files did not change by one line.
// Everything sai-ui asks for that Cadence callers do not supply is resolved
// here, once.
//
// Five mismatches are absorbed below; each has a test in
// `tests/unit/components/ui/button-sai-ui-adapter-13204.test.tsx`.
//
// 1. sai-ui requires a `trackingId` on every Button ("Button: Empty Tracking
//    ID" in its anti-patterns doc) and it is a REQUIRED prop. Adding it to 74
//    call sites is exactly the 1,600-file migration this stage exists to
//    avoid, so it is derived here from the author-controlled props the call
//    site already writes — `id`, then `name`, then `aria-label` — and never
//    resolves to the empty string. See `resolveTrackingId` for why the
//    rendered label is deliberately NOT in that chain.
//
// 1b. (Retired by #16837.) Cadence once painted `text-text-primary-reversed`
//    on the primary fill because its dark-mode `--action-primary` override
//    was a light blue tuned for dark text. Cadence now uses sai-ui's values as
//    shipped, so the label is sai-ui's own `text-white-static` again.
//
// 2. sai-ui expresses its size variants in `--spacing-control-*`. Until
//    #16990 loaded the package's base stylesheet (`src/tokens/sai-ui-base.css`)
//    `h-control-md` / `size-control-md` emitted no CSS here; they do now
//    (32/40/48px for sm/md/lg), but those are not the heights Cadence's call
//    sites lay out against. The adapter therefore states every size's HEIGHT
//    itself, as a fixed `h-*` (`BUTTON_HEIGHT`), plus Cadence's own
//    horizontal padding and type scale. The height is FIXED rather than
//    summed from padding, line-height and border because the sum is what
//    went wrong (#16494): `sm` came out at 32px + border beside a fixed 36px
//    `icon`, so a note header's text pills sat shorter than its More menu.
//    `sm` and `icon` now share one height (36px). `default` (38px) and `lg`
//    (50px) are fixed at the heights their padding already rendered, so the
//    fix moves no other row in the app; `lg` holds the WCAG 2.5.5 floor
//    #7355 put there. Vertical padding is `py-0`
//    so sai-ui's `py-2` cannot shrink the content box under that height.
//    Touch growth is the call site's `pointer-coarse:min-h-11`: `min-height`
//    beats `height`, and it is its own tailwind-merge group, so it survives.
//    sai-ui's size prop is still passed so the icon/standard distinction
//    (and its `[&_svg]` sizing) comes from the design system.
//
// 3. sai-ui takes `ariaLabel`/`ariaLabelledby` as camelCase props and omits
//    the hyphenated ones from its prop type, but its implementation spreads
//    the rest of its props onto the element last. Cadence callers keep
//    writing `aria-label`, it rides through in `rest`, and the camelCase form
//    is passed only where sai-ui's icon-size union demands it.
//
// 4. sai-ui asks for a 2px focus-ring OFFSET without naming an offset colour,
//    which Tailwind v4 resolves to `#fff` — a white halo on the dark theme.
//    `ring-offset-*` is its own tailwind-merge group, so declining to mention
//    it leaves sai-ui's in place; it has to be named. Unlike 1-3 this is NOT
//    a sai-ui mismatch the adapter inherited: the pre-adapter implementation
//    had the same bug. A Button is a filled box, so the offset is TOKENISED
//    rather than collapsed — see `FOCUS_RING_OFFSET` (#13254).
//
// NO FEATURE-FLAG READ BELONGS IN THIS FILE. Until #14972 the SchoolAI
// treatment was a second, attribute-scoped variant map layered over a legacy
// one; the SchoolAI look is now the only look, so each variant names ONE
// treatment. Where a variant differs only in which value a token resolves to,
// that difference belongs in the token layer (#13201), not here.
//
// sai-ui's `AsyncButton` is deliberately NOT used. Cadence's async-button
// convention lives at the call site (`disabled={isPending}` plus a spinner in
// `children`); this primitive has never had a `loading` prop, and adding one
// would change the frozen signature for a path no caller uses.

/**
 * Cadence variant -> sai-ui variant.
 *
 * `default` is Cadence's filled primary action, which is sai-ui's `preferred`
 * (`bg-action-primary`, Cadence's own `--color-action-primary` since #15049) —
 * NOT sai-ui's `default`, which is its outlined variant. Cadence's `outline`
 * takes that one.
 */
const SAI_VARIANT = {
  default: 'preferred',
  destructive: 'destructive',
  ghost: 'ghost',
  outline: 'default',
  secondary: 'ghost',
} as const

/**
 * The treatment per variant, layered over sai-ui's own variant classes.
 * sai-ui's `cn` is `tailwind-merge`, and this string is passed as
 * `className` — which sai-ui merges LAST — so a class here replaces the
 * conflicting one from the variant rather than racing it on stylesheet order.
 * Verified: `twMerge('text-text-primary', 'text-white-static')` yields
 * `text-white-static`.
 *
 * Every variant states its own BORDER, FILL, TEXT COLOUR and HOVER. The map is
 * total on purpose: it is the shared source of truth for `Button` and
 * `ButtonLink`, and `ButtonLink` renders a plain `<a>` with no sai-ui base
 * underneath it, so a treatment left to a vendor base is a treatment only one
 * of the two gets. Caught by the parity cases in
 * `tests/unit/components/ui/button-link.test.tsx`.
 *
 * `default` — sai-ui's own `text-white-static` on `--action-primary`
 * (#16837). Cadence used to restore `text-text-primary-reversed` here because
 * its dark-mode `--action-primary` override was a light blue tuned for dark
 * text; with sai-ui's values as shipped the dark fill is blue-500 and the
 * hover blue-700, where dark text measures 4.46:1 and 3.29:1 but white
 * measures 4.71:1 and 6.38:1 — `tests/unit/tokens/ds-palette-contrast.test.ts`
 * measures the white label on both.
 *
 * `destructive` keeps sai-ui's `text-white-static`: `--action-error` is a fixed
 * `oklch(57.7% 0.245 27.325)` in both themes, and white on it measures
 * 4.77:1 — AA for normal text. Measured, not assumed.
 *
 * `secondary` is the one variant sai-ui does not have: a filled
 * tertiary-surface button, absorbed here with semantic tokens over sai-ui's
 * `ghost`. Its hover is `--action-secondary-hover` — a translucent neutral
 * wash — rather than `--color-action-neutral-hover`, which is the hover
 * for a SOLID `action-neutral` fill and would invert the resting
 * surface.
 *
 * `outline` is sai-ui's 1px `stroke-strong` border; the others carry a 1px
 * TRANSPARENT border so all five variants share one box size and a switch
 * between them does not shift layout.
 */
const VARIANT: Record<NonNullable<ButtonProps['variant']>, string> = {
  default:
    'border border-transparent bg-action-primary text-white-static hover:bg-action-primary-hover',
  secondary:
    'border border-transparent bg-fill-10 text-text-primary hover:bg-action-secondary-hover',
  outline:
    'border border-stroke-strong bg-transparent text-text-primary hover:bg-action-secondary-hover',
  ghost:
    'border border-transparent bg-transparent text-text-primary hover:bg-action-secondary-hover',
  destructive:
    'border border-transparent bg-action-error text-white-static hover:bg-action-error-hover',
}

/** Cadence size -> sai-ui size. */
const SAI_SIZE = {
  default: 'md',
  icon: 'icon-md',
  lg: 'lg',
  sm: 'sm',
} as const

/**
 * The fixed height of each size (#16494; adapter comment item 2).
 *
 * `sm` and `icon` are one height (36px) so a row that mixes them is level.
 * `default` (38px) and `lg` (50px) are fixed at the heights their padding and
 * 1px border always rendered, so fixing `sm` moves no other Button in the
 * app; `lg` stays above the 44px WCAG 2.5.5 floor (#7355). Exported
 * for a non-Button control that must match a Button — the note header's
 * live-recording pill, and `SkeletonButton`.
 */
export const BUTTON_HEIGHT = {
  default: 'h-9.5',
  icon: 'h-9',
  lg: 'h-12.5',
  sm: 'h-9',
} as const satisfies Record<NonNullable<ButtonProps['size']>, string>

/**
 * The geometry every Cadence call site lays out against: a fixed height,
 * then horizontal padding and the type scale. `py-0` replaces sai-ui's
 * `py-2`, which under a fixed height only shrinks the content box.
 */
const SIZE_GEOMETRY: Record<NonNullable<ButtonProps['size']>, string> = {
  default: `${BUTTON_HEIGHT.default} px-4 py-0 text-sm`,
  icon: `${BUTTON_HEIGHT.icon} w-9 p-0`,
  lg: `${BUTTON_HEIGHT.lg} px-6 py-0 text-base`,
  sm: `${BUTTON_HEIGHT.sm} px-3 py-0 text-sm`,
}

/**
 * Gives sai-ui's focus-ring offset a COLOUR (#13254).
 *
 * sai-ui's base string carries `focus-visible:ring-offset-2` but names no
 * ring-offset COLOUR. Tailwind v4 declares `--tw-ring-offset-color` with an
 * initial value of `#fff` (`@property --tw-ring-offset-color{syntax:"*";
 * inherits:false;initial-value:#fff}`), and the compiled stylesheet resolves
 * it to exactly that, so the offset painted a 2px WHITE band between the
 * control and its ring — invisible on light, a halo on dark.
 *
 * `ring-offset-*` is its own tailwind-merge group, so an adapter that simply
 * declines to mention it does NOT drop sai-ui's: the class survives the merge.
 * The offset has to be NAMED to be affected at all.
 *
 * THE TWO CLASSES BELOW MAY NOT BE SEPARATED, for the same reason
 * `input.tsx:143-147` gives: `ring-offset-2` alone is the white band this
 * constant exists to remove. `ring-offset-2` is restated here rather than left
 * to sai-ui's base so that the pair is legible in one place and a future edit
 * cannot drop the colour while leaving the width to arrive invisibly from the
 * package.
 *
 * WHY A COLOURED OFFSET AND NOT `ring-offset-0`.
 *
 * This repo already has a rule for choosing between the two, written at
 * `sort-header.tsx:203-212`: `input.tsx` and `textarea.tsx` pair the offset
 * with `dark:ring-offset-surface-elevated` "because they are boxes with a known
 * background", and `sort-header` collapses it to `ring-offset-0` only because
 * it is bare text inside a consumer's `<th>` whose background varies, so there
 * is no correct colour to name. A Button is a filled box with a known
 * background, so it is in the `input.tsx` camp.
 *
 * The precedent is this exact component, not just inputs:
 * `table-pagination.tsx:117` imports `Button as SaiButton` from
 * `@sai/ui/components` and pairs `dark:ring-offset-surface-elevated`
 * with that Button's own base `ring-offset-2` at `:161-164`. The question was
 * already decided for a sai-ui Button; this makes the adapter agree with it
 * rather than diverge.
 *
 * #13254 originally prescribed `ring-offset-0`, "exactly as #13214 does" for
 * `back-link.tsx` and `page-tabs.tsx`. Those two are bare-text controls — a
 * link and a tab, neither with a fill — so `ring-offset-0` is right for them;
 * the issue generalised a bare-text rule to a filled box. Its stated
 * justification does not survive either: "the pre-adapter Button drew no ring
 * offset" is false. Read it with
 *
 *     git show 3e8c23eb2b:src/components/ui/button.tsx | sed -n '33p'
 *
 * (`git show <rev>:<path>` takes no `:line` suffix — the suffix becomes part
 * of the path and the command fails), which prints
 * `focus-visible:ring-offset-2`,
 * also uncoloured, with no `--tw-ring-offset-color` override anywhere in
 * `globals.css` or `src/tokens/`. So the white band predates #13204, and the
 * truest restoration of the pre-adapter look is a tokenised offset rather than
 * none. Acceptance criterion 1 was amended to say so.
 *
 * The measured cost of `ring-offset-0` on a filled Button, which is what this
 * choice avoids: the ring (`stroke-attention`) would abut the `default`
 * variant's fill (`action-primary`) at 1.12:1 on light and 1.28:1 on dark —
 * two nearly identical blues, so the ring is not distinguishable from the
 * button it rings. On light specifically the white offset was never a bug:
 * `#fff` IS the page surface there.
 *
 * The ring itself is untouched — `stroke-attention` is defined in both
 * `src/tokens/sai-ui-names.css` and the package's `dist/styles/colors.css`, so
 * the ring COLOUR was never part of this bug.
 *
 * Shared with the anchor half: `CONTROL_BASE` composes this constant rather
 * than a bare `ring-offset-2`, so `ButtonLink` — which carries the same
 * variant fills — focuses identically to `Button`. The `ring-offset-0` camp
 * above stays the right answer for bare-text links like `back-link.tsx`; a
 * `ButtonLink` is not one of those.
 *
 * Merged before `className`, so a call site can still override the offset —
 * but it MUST keep a `ring-offset-<token>` COLOUR alongside any width it
 * names. A bare `focus-visible:ring-offset-4` at a call site re-creates a
 * wider version of the white band this constant exists to remove.
 */
const FOCUS_RING_OFFSET =
  'focus-visible:ring-offset-2 dark:ring-offset-surface-elevated'

/** Used when a button carries no `id`, `name` or `aria-label`. */
const FALLBACK_TRACKING_ID = 'cadence-ui-button'

/** Lowercase, hyphen-separated, no leading/trailing separators. */
function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Resolve sai-ui's required `trackingId` from props the call site already
 * passes. Must never return an empty string.
 *
 * **Author-controlled props only — deliberately NOT the rendered label.** An
 * earlier revision fell through to the button's own children, which reads
 * whatever the component happens to render into an analytics attribute. Two
 * concrete consequences, both found in this repo:
 *
 * - `src/components/meetings/aor-picker.tsx:135` builds its label from
 *   `getSentenceLabel('responsibility')`, which is ORG-CONFIGURABLE terminology. A
 *   per-org string would be emitted as the tracking id, so the same control
 *   reports under a different identity in each tenant.
 * - Any label interpolating a count or a person's name (`Delete 3 items`)
 *   mints a fresh id per render, which is unbounded cardinality in PostHog for
 *   what is one button.
 *
 * `id`, `name` and `aria-label` are written by the author and are stable by
 * construction, which is what a tracking id is for. The cost is that a button
 * carrying none of the three reports as `FALLBACK_TRACKING_ID` — accepted,
 * because a shared stable id is a smaller analytics problem than an unstable
 * or tenant-varying one, and because the value it replaces was never reliable:
 * an element child (`<Button><span>Save</span></Button>`) already produced no
 * label text at all. A call site that wants its own id gives the button an
 * `id` or an `aria-label`.
 */
function resolveTrackingId(props: ButtonProps): string {
  const candidates = [props.id, props.name, props['aria-label']]

  for (const candidate of candidates) {
    if (!candidate) continue
    const slug = slugify(candidate)
    if (slug) return slug
  }

  return FALLBACK_TRACKING_ID
}

// ============================================================================
// Shared visual contract — the anchor half
// ============================================================================

/**
 * The layout, focus and disabled classes sai-ui's `Button` supplies from its
 * own cva base. `Button` gets them for free by rendering `SaiButton`; a plain
 * `<a>` does not, so `buttonClassName` has to state them.
 *
 * They are the BASE only — no variant colour and no radius. Those come from
 * `VARIANT` and the radius in `buttonClassName`, which is what keeps the
 * treatment defined exactly once for both the button and the link.
 *
 * The focus ring is sai-ui's own `ring-stroke-attention`, spelled here so the
 * anchor focuses exactly like the sai-ui `Button` it stands in for.
 *
 * The ring OFFSET comes from `FOCUS_RING_OFFSET` rather than a bare
 * `ring-offset-2`, for the reason #13254 gives at that constant: an
 * uncoloured offset resolves to `#fff` and paints a 2px white band on the
 * dark theme. A `ButtonLink` carries the same variant fills a `Button` does,
 * so it is a filled box by the same argument and takes the same tokenised
 * offset — collapsing it to `ring-offset-0` here would make the anchor and
 * button forms distinguishable to keyboard users, which is what this whole
 * shared base exists to prevent.
 */
const CONTROL_BASE = cn(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium',
  'transition-colors [&_svg]:pointer-events-none [&_svg]:shrink-0',
  'focus-visible:outline-none focus-visible:ring-2',
  FOCUS_RING_OFFSET,
  'focus-visible:ring-stroke-attention',
  'aria-disabled:pointer-events-none aria-disabled:opacity-50',
)

export interface ButtonClassNameOptions {
  className?: string
  size?: ButtonProps['size']
  variant?: ButtonProps['variant']
}

/**
 * The shared button visual contract, as a class string.
 *
 * This exists so `ButtonLink` can render a semantic `<a>` that is
 * indistinguishable from a `<Button>` WITHOUT restating the variant classes —
 * it composes the same `SIZE_GEOMETRY` and `VARIANT` maps the component above
 * uses. A second copy of
 * those strings is the drift this helper exists to prevent; that is exactly
 * what `focus-item.tsx` grew when the primitive was briefly absent, and a
 * unit case now pins the two against each other.
 *
 * NOT used by `Button` itself. `Button` renders sai-ui's component, which
 * applies its own base internally and merges this layer's `className` last;
 * feeding it `CONTROL_BASE` as well would restate a base it already has.
 * The half that must not diverge — the variant and size maps — is shared, and
 * that sharing is what the pinning test asserts.
 */
export function buttonClassName({
  className,
  size = 'default',
  variant = 'default',
}: ButtonClassNameOptions = {}): string {
  return cn(
    CONTROL_BASE,
    'rounded-full',
    SIZE_GEOMETRY[size],
    VARIANT[variant],
    className,
  )
}

// ============================================================================
// `asChild`: keeping sai-ui's button-only attributes off a non-button child
// ============================================================================

/**
 * The attributes sai-ui's Button hardcodes onto the element it renders that
 * belong to a `<button>` and to nothing else (#13862).
 *
 * `dist/components/button.js` writes `type: "button"` on `Comp`, and under
 * `asChild` that `Comp` is Radix's `Slot`, which CLONES THE CHILD with those
 * props. So the attribute landed on the `<a>` that `next/link` renders, where
 * it is not a valid attribute at all — and it is the attribute #13862's
 * hydration diff names.
 *
 * It is NOT what made that page's server and client disagree, which matters
 * because believing it was is how this stops here: the disagreement was the
 * Slot-versus-unresolved-child failure written up at `asChild`'s own docs
 * above. Both were real; only one of them is fixable in this file.
 *
 * ONE entry today, and a list rather than a special case because the failure
 * generalises: any button-only prop the package adds later reaches every
 * `asChild` child the same way, and the pinned attribute set in
 * `tests/unit/components/ui/button-aschild-anchor-type-13862.test.tsx` is
 * what makes the next one visible.
 */
const VENDOR_BUTTON_ONLY_PROPS = ['type'] as const

/**
 * Neutralise `VENDOR_BUTTON_ONLY_PROPS` on an `asChild` child, so sai-ui's
 * hardcoded values cannot reach a non-button element.
 *
 * THE CHILD IS THE ONLY LAYER THAT CAN WIN THIS. `mergeProps` in
 * `@radix-ui/react-slot` resolves a collision as `{...slotProps,
 * ...childProps}` — child wins for every key PRESENT in its props, including
 * one whose value is `undefined`, and React 19's `cloneElement` assigns an
 * `undefined` config value rather than falling back to `defaultProps` (that
 * fallback is gone). So cloning the child with `type: undefined` is what makes
 * React omit the attribute; withholding `type` from the wrapper — which the
 * adapter already does — cannot, because the value comes from inside the
 * package.
 *
 * Two children are deliberately left alone:
 *
 * - an intrinsic `<button>`, where `type` is meaningful and dropping it would
 *   change what an enclosing form does;
 * - any child that names its own `type`, which is the call site speaking.
 *   Only the vendored default is neutralised.
 *
 * A COMPONENT child (`next/link`'s `Link`, and anything else that forwards its
 * props) is treated as non-button: the element it renders is unknowable here,
 * and the whole population of such call sites renders anchors. A component
 * that really does render a `<button>` and wants the attribute writes
 * `type="button"` on itself, which the rule above preserves.
 */
/**
 * Say out loud, in development, what `Slot` is about to do silently.
 *
 * The `asChild` docs above forbid the server-component call site, and until
 * this warning existed that rule was enforced by prose alone while its failure
 * mode was a control MISSING from the page with no error, no fallback and no
 * console output — the most expensive shape a rule can have. `Slot` applies
 * exactly the `isValidElement` test below and returns `null` when it fails, so
 * this fires on precisely the inputs that render nothing: a Flight reference
 * handed down from a server component, and the plain-text misuse
 * (`<Button asChild>Save</Button>`).
 *
 * Dev-only and non-throwing, per the precedent in
 * `src/components/today/ai-widget.tsx`: a diagnostic must not become a second
 * way for the control to disappear.
 */
function warnUnslottableChild(children: ReactNode): void {
  if (process.env.NODE_ENV === 'production') return

  console.warn(
    '[Button] `asChild` received a child that `Slot` cannot clone, so it ' +
      'will render NOTHING. Two causes: the call site is a SERVER component ' +
      '(use `ButtonLink` from @/components/ui/button-link instead — see the ' +
      '`asChild` prop docs), or the child is text rather than a single ' +
      'element.',
    children,
  )
}

function withoutVendorButtonOnlyProps(children: ReactNode): ReactNode {
  if (!isValidElement(children)) {
    warnUnslottableChild(children)
    return children
  }
  if (children.type === 'button') return children

  const childProps = children.props as Record<string, unknown>
  const neutralised: Record<string, undefined> = {}

  for (const prop of VENDOR_BUTTON_ONLY_PROPS) {
    if (prop in childProps) continue
    neutralised[prop] = undefined
  }

  if (Object.keys(neutralised).length === 0) return children

  return cloneElement(children as ReactElement<Record<string, unknown>>, {
    ...neutralised,
  })
}

// ============================================================================
// Button Component (React 19 pattern - ref as prop)
// ============================================================================

function Button(props: ButtonProps) {
  const {
    className,
    variant = 'default',
    size = 'default',
    ref,
    asChild,
    ...rest
  } = props

  const trackingId = resolveTrackingId(props)
  const mergedClassName = cn(
    'rounded-full',
    SIZE_GEOMETRY[size],
    VARIANT[variant],
    FOCUS_RING_OFFSET,
    className,
  )

  // `{...rest}` goes FIRST, and the adapter's own props after it. JSX resolves
  // a duplicate key to the LAST spelling, so the previous order let anything
  // that reached `rest` overwrite what this adapter computed. `trackingId` is
  // the one that matters: `ButtonProps` does not declare it, but TypeScript
  // does not excess-property-check a SPREAD, so
  // `<Button {...{ trackingId: '' }} />` compiles (verified with tsc) and used
  // to land an empty tracking id on the element — sai-ui's "Button: Empty
  // Tracking ID" anti-pattern, arriving through the one door this adapter
  // exists to hold shut. Nothing is lost by the reorder: `className`,
  // `variant`, `size` and `ref` are destructured out of `rest` above, so they
  // cannot collide, and the hyphenated `aria-*` attributes ride through `rest`
  // to sai-ui, which spreads its own `restProps` last.
  //
  // `type` restores the NATIVE default. A bare `<button>` — which is what this
  // primitive rendered before #13204 — submits its enclosing form; sai-ui
  // hardcodes `type: "button"` instead. Leaving that in place would silently
  // stop a typeless Button inside a `<form>` from submitting, which is a
  // consumer behaviour change, and this slice's contract is that consumers do
  // not change. An explicit caller `type` still wins. Today's three typeless
  // Buttons all sit outside a form, so this is a no-op on the current tree and
  // a guard on the next call site.
  const controlled = {
    className: mergedClassName,
    ref,
    trackingId,
    // Withheld under `asChild`: the child is the element, and `type` on an
    // anchor is meaningless. See the prop's own docs for the rest.
    //
    // Withholding it is NECESSARY BUT NOT SUFFICIENT (#13862): sai-ui writes
    // its own `type: "button"` inside the package and its Slot clones the
    // child with it, so the child has to carry the neutralising value. The
    // `children` override below is what does that — it comes after `{...rest}`
    // in both JSX spreads, so it replaces the caller's own `children`.
    ...(asChild
      ? {
          asChild,
          children: withoutVendorButtonOnlyProps(rest.children),
        }
      : { type: props.type ?? ('submit' as const) }),
    variant: SAI_VARIANT[variant],
  }

  // Icon-sized buttons take a separate branch because sai-ui's `ButtonProps`
  // is a union that requires an accessible name alongside an `icon-*` size.
  // `rest` still carries the hyphenated `aria-*` attributes and sai-ui spreads
  // it last, so the DOM is identical either way — including the empty-string
  // case, which sai-ui drops rather than rendering an empty `aria-label`.
  if (size === 'icon') {
    const ariaLabelledby = props['aria-labelledby']

    if (ariaLabelledby) {
      return (
        <SaiButton
          {...rest}
          {...controlled}
          ariaLabelledby={ariaLabelledby}
          size={SAI_SIZE.icon}
        />
      )
    }

    return (
      <SaiButton
        {...rest}
        {...controlled}
        ariaLabel={props['aria-label'] ?? ''}
        size={SAI_SIZE.icon}
      />
    )
  }

  return <SaiButton {...rest} {...controlled} size={SAI_SIZE[size]} />
}

export { Button }
